import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Select, DatePicker, Upload, Button, Space, message, Typography, Divider } from "antd";
import { UploadOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { getReq, postReq } from "../api";
import moment from "moment";

const { Text } = Typography;

export default function CreateCampaignModal({ open, onClose, selectedSpocIds, onSuccess }) {
  const [form] = Form.useForm();
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      fetchTemplates();
      form.resetFields();
      setFileList([]);
    }
  }, [open]);

  const fetchTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const res = await getReq("/common/templates", {});
      if (res.status === 1) {
        setTemplates(res.data || []);
      } else {
        message.error("Failed to load templates");
      }
    } catch (error) {
      console.error(error);
      message.error("Error loading email templates");
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleSubmit = async (values) => {
    if (selectedSpocIds.length === 0 && fileList.length === 0) {
      message.error("Please select recipients from the SPOC tab or upload an Excel file.");
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("name", values.name);
      formData.append("templateId", values.templateId);

      if (values.scheduledAt) {
        formData.append("scheduledAt", values.scheduledAt.toISOString());
      } else {
        formData.append("scheduledAt", new Date().toISOString());
      }

      if (selectedSpocIds && selectedSpocIds.length > 0) {
        formData.append("spocIds", JSON.stringify(selectedSpocIds));
      }

      if (fileList.length > 0) {
        formData.append("excel", fileList[0].originFileObj);
      }

      const res = await postReq("/common/campaigns", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (res.status === 1) {
        message.success(res.message || "Campaign created and scheduled!");
        onClose();
        onSuccess();
      } else {
        message.error(res.message || "Failed to create campaign");
      }
    } catch (error) {
      console.error(error);
      message.error("An error occurred while launching the campaign");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title="Launch New Email Campaign"
      onCancel={onClose}
      footer={null}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          scheduledAt: moment(),
        }}
        style={{ marginTop: 16 }}
      >
        <Form.Item
          name="name"
          label="Campaign Name"
          rules={[{ required: true, message: "Please enter a campaign name!" }]}
        >
          <Input placeholder="e.g., Summer Lead Gen Blast" />
        </Form.Item>

        <Form.Item
          name="templateId"
          label="Select Email Template"
          rules={[{ required: true, message: "Please select a template!" }]}
        >
          <Select
            loading={loadingTemplates}
            placeholder="Select a template..."
            options={templates.map((t) => ({ value: t._id, label: t.name }))}
          />
        </Form.Item>

        <Form.Item name="scheduledAt" label="Schedule Campaign (Start Time)">
          <DatePicker
            showTime
            format="YYYY-MM-DD HH:mm"
            style={{ width: "100%" }}
            disabledDate={(current) => current && current < moment().startOf("day")}
          />
        </Form.Item>

        <Divider style={{ margin: "16px 0" }} />

        {/* Recipients Summary */}
        <div style={{ marginBottom: 16 }}>
          <Text strong>Recipients Source:</Text>
          <div style={{ marginTop: 8 }}>
            {selectedSpocIds.length > 0 ? (
              <div style={{ padding: "8px 12px", background: "#f6ffed", border: "1px solid #b7eb8f", borderRadius: 6 }}>
                <Text type="success" strong>{selectedSpocIds.length} SPOCs selected</Text> from the active list.
              </div>
            ) : (
              <div style={{ padding: "8px 12px", background: "#fffbe6", border: "1px solid #ffe58f", borderRadius: 6 }}>
                <InfoCircleOutlined style={{ color: "#faad14", marginRight: 8 }} />
                <Text type="warning">No SPOCs selected.</Text> You must upload an Excel file below.
              </div>
            )}
          </div>
        </div>

        <Form.Item
          label={selectedSpocIds.length > 0 ? "Or Upload Excel to add/replace recipients" : "Upload Excel File with Recipients"}
          extra="Excel should contain columns like 'Email', 'First Name', 'Last Name', and 'Company Name'."
        >
          <Upload
            accept=".csv,.xls,.xlsx"
            maxCount={1}
            beforeUpload={() => false}
            fileList={fileList}
            onChange={(info) => setFileList(info.fileList.slice(-1))}
          >
            <Button icon={<UploadOutlined />}>Select Excel File</Button>
          </Upload>
        </Form.Item>

        <Form.Item style={{ marginBottom: 0, textAlign: "right", marginTop: 24 }}>
          <Space>
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={submitting}>
              Launch Campaign
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
}
